import format from 'date-fns/format'
import omit from 'lodash/omit.js'
import debounce from 'lodash/debounce.js'

import {
  getState,
  setForm,
  updateForm,
  removeForm,
  formsSelector,
  useSelector,
  onlyIfForm,
} from '../../../store.js'
import ConfirmModal from '../../../common/components/Modal/ConfirmModal.js'
import TextArea from '../../../common/components/TextArea.js'
import apiverson from '../../../common/apiverson.js'
import {showMessageToast} from '../../Header/Toast/index.js'
import {setStocktake} from '../../../data/stocktakes.js'
import {getRealDate} from '../../../common/date.js'
import TextInput from '../../../common/components/TextInput.js'
import {isPresent} from '../../../common/utils.js'
import {
  Count,
  IfPlural,
  IfSingle,
  Plural,
  PluralBlock,
} from '../../../common/components/Plural.js'
import {refreshStocktakeList} from '../stocktakeListFunctions.js'
import {navigateToStocktakeDetailPage} from '../../StocktakeDetailPage/stocktakeDetailFunctions.js'
import {buildPath, stringIt} from '../../../common/querystring.js'
import ButtonPrimary from '../../../common/components/Button/ButtonPrimary.js'
import Zelect from '../../../common/components/Zelect.js'
import {
  defaultWarehouseSelector,
  warehouseOptionsSelector,
} from '../../../data/warehouses.js'
import Checkbox from '../../../common/components/Checkbox.js'
import {isKitSelector} from '../../../data/products.js'
import {API_PRODUCT_KIT} from '../../../common/constants/Products.js'
import api from '../../../common/api.js'

const MODAL_FORM = 'create_stocktake_modal'

export async function showCreateStocktakeModal({
  skus = [],
  productListQueryParams = null,
  productListCount = 0,
} = {}) {
  const date = getRealDate()
  const warehouse = defaultWarehouseSelector(getState())

  setForm(MODAL_FORM, {
    referenceID: format(date, 'yyyyMMdd-HHmmss'),
    referenceIDIsDuplicated: false,
    warehouse_id: warehouse.id,
    autoStart: false,
    internal_notes: '',
    productListQueryParams:
      skus.length === 0 && productListQueryParams
        ? omit(productListQueryParams, ['limit', 'offset'])
        : null,
    skus,
    productListCount,
    countOfKitSKUs: skus
      .map((sku) => isKitSelector(getState(), {sku}))
      .filter((v) => v).length,
    hasNoKitFilter:
      skus.length === 0 && productListQueryParams
        ? productListQueryParams[API_PRODUCT_KIT] === false
        : false,
    hasKitFilter:
      skus.length === 0 && productListQueryParams
        ? productListQueryParams[API_PRODUCT_KIT]
        : false,
    isSaving: false,
    serverError: null,
  })

  await checkForKitsInQuery()
  await checkStocktakeReferenceID()
}

export function updateModalForm(updates) {
  updateForm(MODAL_FORM, updates)
}

export function closeModal() {
  removeForm(MODAL_FORM)
}

export function modalFormSelector(state) {
  return formsSelector(state)[MODAL_FORM]
}

export function errorsSelector(state) {
  const {referenceID, referenceIDIsDuplicated} = modalFormSelector(state)
  const errors = {}

  if (!isPresent(referenceID)) {
    errors.referenceID = 'Stocktake ID is required'
    errors.preventSave = true
  } else if (referenceIDIsDuplicated) {
    errors.referenceID = 'Stocktake ID is already in use'
    errors.preventSave = true
  }

  return errors
}

export async function checkForKitsInQuery() {
  const {productListQueryParams, hasNoKitFilter} = modalFormSelector(getState())

  if (!productListQueryParams || hasNoKitFilter) {
    return
  }

  try {
    updateModalForm({isSaving: true, serverError: null})

    const {
      json: {count: countOfKitSKUs},
    } = await api.get('/product', {
      ...productListQueryParams,
      [API_PRODUCT_KIT]: true,
      limit: 0,
    })

    updateModalForm({countOfKitSKUs})
  } catch (err) {
    updateModalForm({
      serverError: `Error loading Create Stocktake modal: ${
        err.message || err.error_message
      }`,
    })
  } finally {
    updateModalForm({isSaving: false})
  }
}

export async function checkStocktakeReferenceID() {
  const token = {}
  checkStocktakeReferenceID.token = token

  const {referenceID} = modalFormSelector(getState())

  if (!referenceID) {
    updateModalForm({referenceIDIsDuplicated: false})

    return
  }

  try {
    updateModalForm({isSaving: true, serverError: null})

    await apiverson.head(`/stocktake/${encodeURIComponent(referenceID)}`)

    if (token !== checkStocktakeReferenceID.token) {
      return
    }

    updateModalForm({referenceIDIsDuplicated: true})
  } catch (err) {
    if (token !== checkStocktakeReferenceID.token) {
      return
    }

    if (err.response && err.response.status === 404) {
      updateModalForm({referenceIDIsDuplicated: false})
    } else {
      updateModalForm({
        serverError: `Error checking stocktake session ID: ${
          err.message || err.error_message
        }`,
      })
    }
  } finally {
    if (token === checkStocktakeReferenceID.token) {
      updateModalForm({isSaving: false})
    }
  }
}

const debounceCheckStocktakeReferenceID = debounce(
  checkStocktakeReferenceID,
  500,
)

export async function createStocktake({goToStocktakeDetails} = {}) {
  try {
    const {
      referenceID,
      warehouse_id,
      internal_notes,
      autoStart,
      skus,
      productListQueryParams,
    } = modalFormSelector(getState())

    updateModalForm({isSaving: true, serverError: null})

    const {json: stocktake} = await apiverson.post('/stocktake', {
      reference_id: referenceID,
      warehouse_id,
      internal_notes,
      ...(productListQueryParams
        ? {criteria: stringIt(productListQueryParams)}
        : skus.length
          ? {criteria: {sku: skus}}
          : undefined),
    })

    setStocktake(stocktake)

    if (autoStart) {
      await apiverson.post(
        buildPath(['stocktake', stocktake.reference_id, 'start']),
        {
          criteria: {},
        },
      )
    }

    showMessageToast('Stocktake was successfully created.')

    closeModal()

    if (goToStocktakeDetails) {
      navigateToStocktakeDetailPage(stocktake.reference_id)
    } else {
      await refreshStocktakeList()
    }
  } catch (err) {
    updateModalForm({
      serverError: `Error creating stocktake: ${err.message || err.error_message}`,
      isSaving: false,
    })
  }
}

function CreateStocktakeModal({form}) {
  const errors = useSelector(errorsSelector)
  const warehouseOptions = useSelector(warehouseOptionsSelector)
  const totalToBeAdded = form.skus.length + form.productListCount

  return (
    <ConfirmModal
      title="Create Stocktake"
      className="modal--fit-screen-sm"
      confirmText="Create"
      cancelText="Cancel"
      onConfirm={() => createStocktake()}
      MiddleButtons={() => (
        <ButtonPrimary
          alt
          className="margin-right-10"
          isDisabled={errors.preventSave}
          isLoading={form.isSaving}
          onClick={() => createStocktake({goToStocktakeDetails: true})}
        >
          Create and View Stocktake
        </ButtonPrimary>
      )}
      onCancel={() => closeModal()}
      isSaving={form.isSaving}
      error={form.serverError}
    >
      <div className="divider--bottom padding-bottom-20 margin-bottom-20">
        {totalToBeAdded === 0 ? (
          <p className="fs-01 lh-md margin-bottom-0">
            <strong>Create an empty stocktake and add SKUs later.</strong>
          </p>
        ) : (
          <>
            {form.productListQueryParams && (
              <>
                <PluralBlock count={totalToBeAdded}>
                  <p className="fs-01 lh-md margin-bottom-0">
                    <strong>
                      Let’s add all filtered SKUs (<Count />) to a new
                      stocktake.
                    </strong>
                  </p>
                </PluralBlock>
              </>
            )}
            {!form.productListQueryParams && (
              <>
                <PluralBlock count={totalToBeAdded}>
                  <p className="fs-01 lh-md margin-bottom-0">
                    <strong>
                      <IfPlural>
                        <Count /> selected SKUs
                      </IfPlural>
                      <IfSingle>The selected SKU</IfSingle> will be included in
                      this new stocktake.
                    </strong>
                  </p>
                </PluralBlock>
              </>
            )}
            {!form.hasNoKitFilter && form.countOfKitSKUs > 0 && (
              <>
                {totalToBeAdded > 1 && (
                  <PluralBlock count={form.countOfKitSKUs}>
                    <IfPlural>
                      <p className="fs-01 lh-md margin-top-5 margin-bottom-0">
                        <Count /> of the SKUs to be added are{' '}
                        <Plural word="kit" />.
                      </p>
                    </IfPlural>
                    <IfSingle>
                      <p className="fs-01 lh-md margin-top-5 margin-bottom-0">
                        One of the SKUs to be added is a kit.
                      </p>
                    </IfSingle>
                  </PluralBlock>
                )}
                {totalToBeAdded === 1 && (
                  <p className="fs-01 lh-md margin-top-5 margin-bottom-0">
                    The SKU you’d like to add is a kit.
                  </p>
                )}
                <p className="fs-00 lh-md margin-top-15 margin-bottom-0">
                  <em>
                    {form.hasKitFilter ? (
                      <span>
                        Including kits in a stocktake could cause inventory
                        discrepancies.
                      </span>
                    ) : (
                      <span>
                        To prevent kits from being added to the stocktake,
                        enable the <strong>Not Kits</strong> filter in Product
                        Filters.
                      </span>
                    )}{' '}
                    <a
                      className="btn btn--link v-align-base darkest border-underline"
                      href="https://support.ordoro.com/how-to-use-the-stocktake-feature-in-ordoro/#kits-in-stocktake"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <strong>Learn more</strong>
                    </a>{' '}
                    →
                  </em>
                </p>
              </>
            )}
          </>
        )}
      </div>
      <div className="row">
        <div className="medium-9 columns">
          <ul className="list list--no-style margin-bottom-30">
            <li className="list__item margin-bottom-20">
              <TextInput
                label="Stocktake ID"
                id="reference_id"
                className="input--med-h"
                value={form.referenceID}
                onChange={(value) => {
                  updateModalForm({referenceID: value})

                  debounceCheckStocktakeReferenceID()
                }}
                onBlur={() => debounceCheckStocktakeReferenceID.flush()}
                errorMessage={errors.referenceID}
                prefix="ST-"
              />
            </li>
            <li className="list__item margin-bottom-20">
              <Zelect
                label="Warehouse"
                id="warehouse_id"
                className="input--med-h"
                value={form.warehouse_id}
                onChange={(option) =>
                  updateModalForm({warehouse_id: option.value})
                }
                options={warehouseOptions}
              />
            </li>
            {totalToBeAdded > 0 && (
              <li className="list__item margin-bottom-20">
                <Checkbox
                  id="get_expected_qty"
                  label="Record All Expected Physical On Hand Quantities Now"
                  checked={form.autoStart}
                  onChange={(autoStart) => updateModalForm({autoStart})}
                />
                <p className="fs-n0 lh-md op-75">
                  <em>
                    This option will lock in all CURRENT physical on hand
                    quantities for the SKUs you’re including in this new
                    stocktake.
                  </em>
                </p>
              </li>
            )}
            <li className="list__item margin-bottom-0">
              <TextArea
                className="fs-n0 lh-lg"
                label="Notes for Stocktake"
                id="internal_notes"
                value={form.internal_notes}
                onChange={(internal_notes) => updateModalForm({internal_notes})}
              />
            </li>
          </ul>
        </div>
      </div>
    </ConfirmModal>
  )
}

export default onlyIfForm(CreateStocktakeModal, modalFormSelector)
